import { useState, useEffect, useMemo } from 'react';
import { migrationApi, teamApi, userApi, federationApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { History, Loader2, ArrowRight, Calendar, User } from 'lucide-react';

/**
 * Wave 3 — Migration history viewer.
 * Shows a timeline of all migration audit records for a given entity
 * (athlete | team | professor). Resolves team/professor/federation ids
 * to human-readable names.
 */
export default function MigrationHistoryDialog({ entity, entityType, open, onOpenChange }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookups, setLookups] = useState({ teams: [], professors: [], federations: [] });

  useEffect(() => {
    if (!open || !entity?._id) return;
    setLoading(true);
    Promise.all([
      migrationApi.list({ entity_type: entityType, entity_id: entity._id }),
      teamApi.list(),
      userApi.list({ role: 'professor' }),
      federationApi.list(),
    ])
      .then(([m, t, p, f]) => {
        setRecords(m.data || []);
        setLookups({ teams: t.data || [], professors: p.data || [], federations: f.data || [] });
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [open, entity, entityType]);

  const idMap = useMemo(() => {
    const map = {};
    (lookups.teams || []).forEach((t) => { map[t._id] = `Equipe: ${t.name}`; });
    (lookups.professors || []).forEach((p) => { map[p._id] = `Prof. ${p.name}`; });
    (lookups.federations || []).forEach((f) => { map[f._id] = `Fed. ${f.name}`; });
    return map;
  }, [lookups]);

  const fieldLabel = (f) => ({
    team_id: 'Equipe',
    professor_id: 'Professor',
    federation_ids: 'Federações',
  }[f] || f);

  const renderValue = (v) => {
    if (v === null || v === undefined || v === '') return <span className="text-[#71717A] italic">vazio</span>;
    /* A unificacao de cadastros duplicados grava `new_value` como OBJETO.
       Renderizar objeto no JSX derruba o React ("Objects are not valid as a
       React child") e o app inteiro fica preto: aqui vira texto. */
    if (typeof v === 'object') return <span title={JSON.stringify(v)}>{JSON.stringify(v)}</span>;
    // try to parse as JSON list
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) {
        return <span>{parsed.map((x) => idMap[x] || x).join(', ') || '—'}</span>;
      }
    } catch (e) { /* not JSON */ }
    return <span>{idMap[v] || v}</span>;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#27272A] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="migration-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-['Chivo'] flex items-center gap-2">
            <History size={16} className="text-[#D4AF37]" /> Histórico de Migrações
          </DialogTitle>
          <DialogDescription className="text-[#71717A] text-xs">
            Linha do tempo imutável de todas as alterações estruturais (equipe, professor, federação).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3">
          <div className="text-sm text-[#A1A1AA] mb-3">
            <strong className="text-white">{entity?.name || '—'}</strong>
            <span className="ml-2 text-xs text-[#71717A]">({entityType})</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
          ) : records.length === 0 ? (
            <div className="text-center py-10 text-[#71717A] text-sm">
              <History size={32} className="mx-auto mb-2 opacity-40" />
              Nenhuma alteração registrada ainda.
            </div>
          ) : (
            <ol className="relative border-l-2 border-[#27272A] ml-3">
              {records.map((r) => (
                <li key={r._id} className="ml-6 pb-5" data-testid={`migration-record-${r._id}`}>
                  <span className="absolute -left-1.5 w-3 h-3 bg-[#D4AF37] rounded-full mt-1.5"></span>
                  <div className="bg-[#1A1A1A] border border-[#27272A] rounded-lg p-3">
                    <div className="flex items-center justify-between text-xs text-[#71717A]">
                      <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(r.performed_at)}</span>
                      <span className="px-2 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] uppercase tracking-wider font-bold">
                        {fieldLabel(r.field_changed)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm text-white flex-wrap">
                      {renderValue(r.old_value)}
                      <ArrowRight size={16} className="text-[#D4AF37] shrink-0" />
                      {renderValue(r.new_value)}
                    </div>
                    {r.reason && (
                      <p className="mt-2 text-xs text-[#A1A1AA] italic">&ldquo;{r.reason}&rdquo;</p>
                    )}
                    <div className="mt-2 flex items-center gap-1 text-xs text-[#71717A]">
                      <User size={12} /> {r.performed_by_email || r.performed_by || '—'} ({r.performer_role || '—'})
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
